import { AuthEnums } from "models/enums";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { ServiceConstants } from "utils/constants";
import { AxiosResponse } from "axios";
import { TranslationKeys } from "translations";
import { SupplierFormTypes } from "models/formTypes";
import useIncomingInvoiceService from "./useIncomingInvoiceService";
import useAPIClientHelper from "./useAPIClientHelper";
//#endregion
import { SupplierService } from "services";
import { LocalizationHelperInstance } from "utils/helpers";

function useSupplierService() {
  const { apiClientInstance } = useAPIClientHelper();
  const incomingInvoiceService = useIncomingInvoiceService();

  //#region Supplier
  const getSuppliers = async (
    pagination: WintUtilsWebApi.Filter,
    filterProps: SupplierFormTypes.Filter
  ): Promise<AxiosResponse<WintUtilsWebApi.ListResponse<WintSuperkollAPIModel.IncomingInvoiceSupplierInfo>>> => {
    return SupplierService.filter({
      ...pagination,
      ...reportToAPIFilter(filterProps),
    });
  };

  const reportToAPIFilter = (
    formFilter?: SupplierFormTypes.Filter
  ): Omit<WintSuperkollAPIModel.IncomingInvoiceSupplierFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      name: formFilter?.search,
      invoiceDateFrom: formFilter?.invoiceDate?.start,
      invoiceDateTo: formFilter?.invoiceDate?.end,
      hasRules: formFilter?.hasRules,
      hasUnpaidInvoices: formFilter?.hasUnpaidInvoices,
      numberOfInvoicesFrom: formFilter?.numberOfInvoices?.lowest,
      numberOfInvoicesTo: formFilter?.numberOfInvoices?.highest,
      invoicedAmountFrom: formFilter?.invoicedAmount?.lowest,
      invoicedAmountTo: formFilter?.invoicedAmount?.highest,
    };
  };

  const getSupplier = async (supplierId: number): Promise<WintSuperkollAPIModel.IncomingInvoiceSupplierInfo> => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .IncomingInvoiceClient.GetSupplier(supplierId)
      .then((res) => res.data);
  };

  const getTodos = async (
    supplierId: number,
    filter?: WintUtilsWebApi.Filter
  ): Promise<AxiosResponse<WintUtilsWebApi.ListResponse<WintSuperkollAPIModel.SimpleIncomingInvoiceTodo>>> => {
    return apiClientInstance(AuthEnums.AuthType.Company).IncomingInvoiceClient.GetTodos(
      supplierId,
      filter ?? ServiceConstants.DefaultFilter
    );
  };

  const updateSupplier = (supplierId: number, supplierPut: WintSuperkollAPIModel.IncomingInvoiceSupplierPut) => {
    return apiClientInstance(AuthEnums.AuthType.Company).IncomingInvoiceClient.UpdateSupplier(supplierId, supplierPut);
  };

  //#endregion

  //#region Rules
  const getRules = async (
    supplierId: number,
    filter?: WintUtilsWebApi.Filter
  ): Promise<AxiosResponse<WintUtilsWebApi.ListResponse<WintSuperkollAPIModel.IncomingInvoiceRule>>> => {
    return apiClientInstance(AuthEnums.AuthType.Company).IncomingInvoiceClient.GetRules(
      supplierId,
      filter ?? ServiceConstants.DefaultFilter
    );
  };

  const getRule = async (
    supplierId: number,
    ruleId: number
  ): Promise<AxiosResponse<WintSuperkollAPIModel.IncomingInvoiceRule>> => {
    return apiClientInstance(AuthEnums.AuthType.Company).IncomingInvoiceClient.GetRuleById(supplierId, ruleId);
  };

  const createRule = async (rule: WintSuperkollAPIModel.IncomingInvoiceRulePost): Promise<number> => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .IncomingInvoiceClient.CreateRule(rule)
      .then((res) => res.data);
  };

  const updateRule = async (ruleId: number, rule: WintSuperkollAPIModel.IncomingInvoiceRulePut): Promise<number> => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .IncomingInvoiceClient.UpdateRule(ruleId, rule)
      .then((res) => res.data);
  };
  //#endregion

  //#region Invoice
  const getInvoices = async (
    supplierId: number,
    pagination: WintUtilsWebApi.Filter
  ): Promise<AxiosResponse<WintUtilsWebApi.ListResponse<WintSuperkollAPIModel.IncomingInvoice>>> => {
    return incomingInvoiceService.filter({
      ...pagination,
      onlyMine: false,
      supplierId: supplierId,
    });
  };

  //#endregion

  const getRulePeriodShortText = (period: WintSuperkollAPIModel.IncomingInvoiceRulePeriod): string => {
    return LocalizationHelperInstance.getFormattedMessage(
      period === WintSuperkollAPIModel.IncomingInvoiceRulePeriod.Month
        ? TranslationKeys.Common.Monthly
        : period === WintSuperkollAPIModel.IncomingInvoiceRulePeriod.Year
        ? TranslationKeys.Common.Yearly
        : TranslationKeys.Common.All
    );
  };

  const getRulePeriodText = (rulePeriod: WintSuperkollAPIModel.IncomingInvoiceRulePeriod) => {
    return LocalizationHelperInstance.getFormattedMessage(`Supplier.RulePeriod_${rulePeriod}`);
  };

  const getSuggestedSuppliers = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .IncomingInvoiceClient.GetSuggestedIncomingInvoiceSuppliers()
      .then((res) => res.data ?? []);
  };

  return {
    getSuppliers,
    reportToAPIFilter,
    getSupplier,
    getTodos,
    updateSupplier,
    getRules,
    getRule,
    createRule,
    updateRule,
    getInvoices,
    getRulePeriodShortText,
    getRulePeriodText,
    getSuggestedSuppliers,
  };
}

export default useSupplierService;
